import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { parseNumericValue, parseDate } from '@/services/pmt2Utils'; // Assuming parseDate exists
import { Timestamp } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow, TableHead, TableHeader } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from "@/lib/utils"; // Import cn utility
import { CheckCircle } from 'lucide-react'; // Import CheckCircle icon
import { fetchLatestOldValuesMap } from '@/services/pmt3OldValuesService';

interface SeasonDashboardProps {
  product: PMT2Product | null;
}

type OldValuesMap = Record<string, string>;

const SeasonDashboard: React.FC<SeasonDashboardProps> = ({ product }) => {
  const [oldValuesMap, setOldValuesMap] = useState<OldValuesMap | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // --- Formatting & Utility Helpers ---
  const formatYesNo = (value: any): string => {
      if (value === null || value === undefined || value === '') return '-';
      const str = String(value).toLowerCase();
      if (str === 'yes' || str === 'true' || str === '1') return 'Yes';
      if (str === 'no' || str === 'false' || str === '0') return 'No';
      return '-';
  };

  const formatDefault = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };

  const formatDateMMDDYYYY = (date: Date | null): string => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '-';
      // Use UTC methods to avoid timezone issues if dates are meant to be universal
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = date.getUTCDate().toString().padStart(2, '0');
      const year = date.getUTCFullYear();
      return `${month}/${day}/${year}`;
  };

  const calculateDaysRemaining = (endDateStr: any): string => {
      const endDate = parseDate(endDateStr); // Use the parsing utility
      if (!endDate) return '-';

      const now = new Date();
      // Set both dates to midnight UTC to compare just the date part
      now.setUTCHours(0, 0, 0, 0);
      endDate.setUTCHours(0, 0, 0, 0);

      const diffTime = endDate.getTime() - now.getTime();
      // If end date is in the past or today, return 0 or '-'? Let's return 0 for now.
      if (diffTime <= 0) return '0';

      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Use ceil to include partial days
      return String(diffDays);
  };
  // --- End Helpers ---

  // Fetch Old Values Map
  useEffect(() => {
    const fetchMap = async () => {
      setIsLoading(true);
      setOldValuesMap(null);

      if (product?.companyName && product?.id) {
        try {
          const map = await fetchLatestOldValuesMap(product.companyName, product.id);
          setOldValuesMap(map);
        } catch (error) {
          console.error("Error fetching old values map for Season:", error);
          setOldValuesMap(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchMap();
  }, [product]);

  // Prepare data for rendering
  const currentSeason = product?.['Current Season'];
  const daysLeft = calculateDaysRemaining(product?.['Current Season End']);
  const season1Start = formatDateMMDDYYYY(parseDate(product?.['Season 1 - Start | On Date']));
  const season1End = formatDateMMDDYYYY(parseDate(product?.['Season 1 - End | Off Date']));
  const season2Start = formatDateMMDDYYYY(parseDate(product?.['Season 2 - Start | On Date']));
  const season2End = formatDateMMDDYYYY(parseDate(product?.['Season 2 - End | Off Date']));
  const useAutoDates = formatYesNo(product?.['Use Auto Seasonal Dates']);
  const disablePauseOnSeason = formatYesNo(product?.['Disable Low Inventory Pausing System While On-Season']);

  const oldCurrentSeason = oldValuesMap?.['Current Season'];
  const oldUseAutoDates = oldValuesMap?.['Use Auto Seasonal Dates'];
  const oldDisablePause = oldValuesMap?.['Disable Low Inventory Pausing System While On-Season'];

  const displayOldCurrentSeason = oldCurrentSeason && formatDefault(currentSeason) !== formatDefault(oldCurrentSeason);
  const displayOldUseAutoDates = oldUseAutoDates && formatYesNo(useAutoDates) !== formatYesNo(oldUseAutoDates);
  const displayOldDisablePause = oldDisablePause && formatYesNo(disablePauseOnSeason) !== formatYesNo(oldDisablePause);


  if (!product) {
       return (
            <Card>
                <CardHeader><CardTitle className="text-2xl font-bold">Season</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-500">Select a product to view Season details.</p></CardContent>
            </Card>
        );
  }

  return (
    <Card className="max-w-2xl mx-auto"> {/* Reduced width */}
      <CardHeader className="p-4">
        <CardTitle className="text-2xl font-bold">Season</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-1/2">Item</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Old Value</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {/* Section 1 */}
                <TableRow>
                    <TableCell className="font-medium py-1 px-2">Current Season</TableCell> {/* Reduced padding */}
                    <TableCell className={cn("text-right py-1 px-2", // Reduced padding & conditional formatting
                        !isLoading && (
                            (String(currentSeason).toLowerCase() === 'yes' || String(currentSeason).toLowerCase() === 'on season') ? 'text-green-700 dark:text-green-400' :
                            (String(currentSeason).toLowerCase() === 'no' || String(currentSeason).toLowerCase() === 'off season') ? 'text-yellow-500 dark:text-yellow-400' : ''
                        )
                    )}>
                        {isLoading ? <Skeleton className="h-4 w-20 float-right" /> : (
                             <div className="flex items-center justify-end space-x-1">
                                {!isLoading && (String(currentSeason).toLowerCase() === 'yes' || String(currentSeason).toLowerCase() === 'on season') && <CheckCircle size={16} />}
                                <span>{formatDefault(currentSeason)}</span>
                            </div>
                        )}
                    </TableCell>
                    <TableCell className="text-right py-1 px-2"> {/* Reduced padding */}
                        {isLoading ? <Skeleton className="h-4 w-20 float-right" /> : (
                            displayOldCurrentSeason ? (
                                <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                                    {formatDefault(oldCurrentSeason)}
                                </span>
                            ) : (
                                <span>-</span>
                            )
                        )}
                    </TableCell>
                </TableRow>
                <TableRow className="border-b border-gray-200 dark:border-gray-700"> {/* Line break */}
                    <TableCell className="font-medium py-1 px-2">Days Left in Current Season</TableCell> {/* Reduced padding */}
                    <TableCell className={cn("text-right py-1 px-2", // Reduced padding & conditional formatting (based on Current Season)
                         !isLoading && (
                            (String(currentSeason).toLowerCase() === 'yes' || String(currentSeason).toLowerCase() === 'on season') ? 'text-green-700 dark:text-green-400' :
                            (String(currentSeason).toLowerCase() === 'no' || String(currentSeason).toLowerCase() === 'off season') ? 'text-yellow-500 dark:text-yellow-400' : ''
                        )
                    )}>
                        {isLoading ? <Skeleton className="h-4 w-12 float-right" /> : (
                             <div className="flex items-center justify-end space-x-1">
                                {!isLoading && (String(currentSeason).toLowerCase() === 'yes' || String(currentSeason).toLowerCase() === 'on season') && <CheckCircle size={16} />}
                                <span>{daysLeft}</span>
                            </div>
                        )}
                    </TableCell>
                    <TableCell className="text-right py-1 px-2"><span>-</span></TableCell> {/* Reduced padding */}
                </TableRow>

                 {/* Section 2 Header */}
                 <TableRow className="bg-gray-50 dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <TableCell className="font-semibold text-gray-700 dark:text-gray-200 py-1 px-2">Season</TableCell> {/* Reduced padding */}
                    <TableCell className="font-semibold text-gray-700 dark:text-gray-200 text-right py-1 px-2">Start</TableCell> {/* Reduced padding */}
                    <TableCell className="font-semibold text-gray-700 dark:text-gray-200 text-right py-1 px-2">End</TableCell> {/* Reduced padding */}
                 </TableRow>

                 {/* Section 2 Data */}
                 <TableRow>
                    <TableCell className="font-medium py-1 px-2">Season 1</TableCell> {/* Reduced padding */}
                    <TableCell className="text-right py-1 px-2">{isLoading ? <Skeleton className="h-4 w-16 float-right" /> : season1Start}</TableCell> {/* Reduced padding */}
                    <TableCell className="text-right py-1 px-2">{isLoading ? <Skeleton className="h-4 w-16 float-right" /> : season1End}</TableCell> {/* Reduced padding */}
                 </TableRow>
                 <TableRow className="border-b border-gray-200 dark:border-gray-700"> {/* Line break */}
                    <TableCell className="font-medium py-1 px-2">Season 2</TableCell> {/* Reduced padding */}
                    <TableCell className="text-right py-1 px-2">{isLoading ? <Skeleton className="h-4 w-16 float-right" /> : season2Start}</TableCell> {/* Reduced padding */}
                    <TableCell className="text-right py-1 px-2">{isLoading ? <Skeleton className="h-4 w-16 float-right" /> : season2End}</TableCell> {/* Reduced padding */}
                 </TableRow>

                 {/* Section 3 Header (Optional Spacer/Separator) */}
                 <TableRow className="bg-gray-50 dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <TableCell colSpan={3} className="py-1 px-2">&nbsp;</TableCell> {/* Reduced padding */}
                 </TableRow>

                 {/* Section 3 Data */}
                 <TableRow>
                    <TableCell className="font-medium py-1 px-2">Use Auto Seasonal Dates</TableCell> {/* Reduced padding */}
                    <TableCell className={cn("text-right py-1 px-2", // Reduced padding & conditional formatting
                        !isLoading && (
                            useAutoDates === 'Yes' ? 'text-green-700 dark:text-green-400' :
                            useAutoDates === 'No' ? 'text-yellow-500 dark:text-yellow-400' : ''
                        )
                    )}>
                        {isLoading ? <Skeleton className="h-4 w-10 float-right" /> : (
                             <div className="flex items-center justify-end space-x-1">
                                {!isLoading && useAutoDates === 'Yes' && <CheckCircle size={16} />}
                                <span>{useAutoDates}</span>
                            </div>
                        )}
                    </TableCell>
                    <TableCell className="text-right py-1 px-2"> {/* Reduced padding */}
                        {isLoading ? <Skeleton className="h-4 w-10 float-right" /> : (
                            displayOldUseAutoDates ? (
                                <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                                    {formatYesNo(oldUseAutoDates)}
                                </span>
                            ) : (
                                <span>-</span>
                            )
                        )}
                    </TableCell>
                 </TableRow>
                 <TableRow>
                    <TableCell className="font-medium py-1 px-2">Disable Low Inventory Pausing System While On-Season</TableCell> {/* Reduced padding */}
                    <TableCell className={cn("text-right py-1 px-2", // Reduced padding & conditional formatting (REVERSED)
                        !isLoading && (
                            disablePauseOnSeason === 'No' ? 'text-green-700 dark:text-green-400' : // Green for No
                            disablePauseOnSeason === 'Yes' ? 'text-yellow-500 dark:text-yellow-400' : '' // Yellow for Yes
                        )
                    )}>
                        {isLoading ? <Skeleton className="h-4 w-10 float-right" /> : (
                             <div className="flex items-center justify-end space-x-1">
                                {/* Show CheckCircle only when 'No' (which is now green) */}
                                {!isLoading && disablePauseOnSeason === 'No' && <CheckCircle size={16} />}
                                <span>{disablePauseOnSeason}</span>
                            </div>
                        )}
                    </TableCell>
                    <TableCell className="text-right py-1 px-2"> {/* Reduced padding */}
                        {isLoading ? <Skeleton className="h-4 w-10 float-right" /> : (
                            displayOldDisablePause ? (
                                <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                                    {formatYesNo(oldDisablePause)}
                                </span>
                            ) : (
                                <span>-</span>
                            )
                        )}
                    </TableCell>
                 </TableRow>

            </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default SeasonDashboard;